import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, CreditCard, Tag, UserCog, Activity, Settings, LogOut, BarChart3,
  IndianRupee, BookCheck, UserPlus, Edit2, Trash2, KeyRound, Power, X,
  AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, RefreshCw, Menu,
  Calendar, Clock, Phone, MapPin, Wrench, StickyNote, Inbox, Search,
  ShieldCheck, Flame, ArrowRight, Bell,
  FileText, Package, MessageCircle, Star,
  Shield, HeartPulse, Palette, HardDrive, ScrollText, Ticket, Receipt, ClipboardCheck, Download,
  Edit3, XCircle, Wallet, Lock, Unlock, Crown, Award, Mail, Copy, Pencil, History, Sparkles, Calculator, Headphones, Newspaper, Clapperboard, MonitorPlay,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePanelAuth, formatApiError } from './PanelAuthContext';
import AdminCoupons from '../AdminCoupons';
import {
  ScheduleTab, QuotationsTab, WorkOrdersTab, AMCTab, InventoryTab, ReviewsTab,
} from './Phase4Tabs';
import WhatsAppManagementTab from './WhatsAppManagementTab';
import RecycleBinTab from './RecycleBinTab';
import { DeleteConfirmProvider, useDeleteConfirm } from '../../components/DeleteConfirmDialog';
import IncentivesAdminTab from './IncentivesAdminTab';
import CustomerAccountsTab from './CustomerAccountsTab';
import ServedCustomersTab from './ServedCustomersTab';
import ServiceTicketsAdminTab from './ServiceTicketsAdminTab';
import TicketsAdminTab from './TicketsAdminTab';
import InvoicesAdminTab from './InvoicesAdminTab';
import SpecialOffersAdminTab from './SpecialOffersAdminTab';
import PricingCalculatorAdminTab from './PricingCalculatorAdminTab';
import BlogAdminTab from './BlogAdminTab';
import VideoTestimonialsAdminTab from './VideoTestimonialsAdminTab';
import HeroVideoAdminTab from './HeroVideoAdminTab';
import ThemeCampaignAdminTab from './ThemeCampaignAdminTab';
import HomepageTestimonialsAdminTab from './HomepageTestimonialsAdminTab';
import StaffToolsAdminTab from './StaffToolsAdminTab';
import StaffToolsLauncher from '../../components/StaffToolsLauncher';
import WarrantyAdminTab from './WarrantyAdminTab';
import ApprovalsAdminTab from './ApprovalsAdminTab';
import AnalyticsDashboard from './AnalyticsDashboard';
import {
  PermissionsMatrixTab, SystemHealthTab, BrandingTab, BackupsTab, FullAuditTab, EmailSettingsTab,
} from './SuperAdminTabs';
import PromoBannerTab from './PromoBannerTab';
import PaymentPanelTab from './PaymentPanelTab';
import BookingPipelineTab from './BookingPipelineTab';
import LeadManagementTab from './LeadManagementTab';
import CustomerLoginManagementTab from './CustomerLoginManagementTab';
import { CreateLeadModal, CreateBookingModal, ConvertLeadModal, LeadSourceBadge } from './LeadBookingModals';
import BookingsTimeFilter, { applyTimeFilter } from './BookingsTimeFilter';

const fmtMoney = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function AdminPortal() {
  const { user, panelToken, gwToken, logout, api, bootChecked } = usePanelAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [staffLinkCount, setStaffLinkCount] = useState(0);
  const isSuper = user?.role === 'super_admin';
  useEffect(() => {
    if (isSuper || !panelToken) return;
    api('GET', '/staff-links').then((r) => setStaffLinkCount((r.data.links || []).length)).catch(() => {});
  }, [isSuper, panelToken, api]);

  if (bootChecked && (!gwToken || !panelToken)) return <Navigate to="/official" replace />;
  if (bootChecked && user && !['admin', 'super_admin'].includes(user.role)) return <Navigate to="/official/employee" replace />;

  const handleLogout = async () => {
    await logout(false);
    nav('/official/portal-selection', { replace: true });
  };

  const tabPerms = user?.tab_permissions || [];
  const canSee = (tabId) => isSuper || tabPerms.includes('*') || tabPerms.includes(`tab:${tabId}`);

  // Base admin/operations tabs (filtered by per-admin tab_permissions)
  const ADMIN_TABS = [
    { id: 'overview', label: 'Overview', Icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', Icon: BarChart3 },
    { id: 'pipeline', label: 'Booking Pipeline', Icon: ArrowRight },
    { id: 'lead-management', label: 'Lead Management', Icon: Users },
    { id: 'work-orders', label: 'Work Orders', Icon: Wrench },
    { id: 'approvals', label: 'Approvals', Icon: ClipboardCheck },
    { id: 'warranties', label: 'Warranties', Icon: ShieldCheck },
    { id: 'amc', label: 'AMC', Icon: RefreshCw },
    { id: 'customers', label: 'Customers', Icon: Users },
    { id: 'served-customers', label: 'Served Customers', Icon: Crown },
    { id: 'accounts', label: 'Accounts', Icon: UserCog },
    { id: 'tickets', label: 'Tickets', Icon: Ticket },
    { id: 'service-tickets', label: 'Service Tickets', Icon: AlertCircle },
    { id: 'quotations', label: 'Quotations', Icon: FileText },
    { id: 'invoices', label: 'Invoices', Icon: Receipt },
    { id: 'payments', label: 'Payments', Icon: CreditCard },
    { id: 'coupons', label: 'Coupons', Icon: Tag },
    { id: 'pricing-calc', label: 'Pricing Calculator', Icon: Calculator },
    { id: 'special-offers', label: 'Special Offers', Icon: Sparkles },
    { id: 'blog-news', label: 'Blog & News', Icon: Newspaper },
    { id: 'video-testimonials', label: 'Video Testimonials', Icon: Clapperboard },
    { id: 'hero-videos', label: 'Hero Videos', Icon: MonitorPlay },
    { id: 'seasonal-themes', label: 'Seasonal Themes', Icon: Palette },
    { id: 'inventory', label: 'Inventory', Icon: Package },
    { id: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
    { id: 'reviews', label: 'Reviews', Icon: Star },
    { id: 'homepage-testimonials', label: 'Homepage Reviews', Icon: Users },
    { id: 'employees', label: 'Employees', Icon: UserCog },
    { id: 'incentives', label: 'Incentives', Icon: Award },
    { id: 'recycle-bin', label: 'Recycle Bin', Icon: Trash2 },
    { id: 'audit', label: 'Audit', Icon: Activity },
  ].filter(t => canSee(t.id));

  // Super-admin-only platform tabs
  const SUPER_TABS = isSuper ? [
    { id: 'staff-tools', label: 'EntoRid Staff Tools', Icon: Headphones, group: 'platform' },
    { id: 'promo-banner', label: 'Promo Banner', Icon: Ticket, group: 'platform' },
    { id: 'payment-panel', label: 'Payment Panel', Icon: Wallet, group: 'platform' },
    { id: 'permissions', label: 'Permissions', Icon: Shield, group: 'platform' },
    { id: 'customer-login-management', label: 'Customer Login Management', Icon: KeyRound, group: 'platform' },
    { id: 'system-health', label: 'System Health', Icon: HeartPulse, group: 'platform' },
    { id: 'branding', label: 'Branding', Icon: Palette, group: 'platform' },
    { id: 'backups', label: 'Backups', Icon: HardDrive, group: 'platform' },
    { id: 'email-settings', label: 'Email Settings', Icon: Mail, group: 'platform' },
    { id: 'full-audit', label: 'Full Audit', Icon: ScrollText, group: 'platform' },
    { id: 'settings', label: 'Settings', Icon: Settings, group: 'platform' },
  ] : [];

  // Non-super admins with granted staff links get a launcher-only tab.
  const STAFF_TAB = (!isSuper && staffLinkCount > 0) ? [{ id: 'staff-tools', label: 'EntoRid Staff Tools', Icon: Headphones }] : [];

  const TABS = [...ADMIN_TABS, ...STAFF_TAB, ...SUPER_TABS];
  const NAV_GROUPS = [
    ['Operations', ['overview', 'analytics', 'pipeline', 'lead-management', 'bookings', 'work-orders', 'approvals', 'customers', 'served-customers']],
    ['Service Delivery', ['warranties', 'amc', 'tickets', 'service-tickets', 'inventory']],
    ['Sales & Finance', ['quotations', 'invoices', 'payments', 'coupons', 'pricing-calc', 'special-offers', 'payment-panel', 'promo-banner']],
    ['Website & Marketing', ['blog-news', 'video-testimonials', 'hero-videos', 'seasonal-themes', 'whatsapp', 'reviews', 'homepage-testimonials']],
    ['People & Access', ['employees', 'incentives', 'staff-tools', 'permissions', 'customer-login-management']],
    ['Administration', ['recycle-bin', 'audit', 'full-audit', 'system-health', 'branding', 'backups', 'email-settings', 'settings']],
  ];

  return (
    <DeleteConfirmProvider api={api}>
    <div className="min-h-screen bg-slate-50 flex flex-col md:h-dvh md:min-h-0 md:overflow-hidden md:flex-row" data-testid="admin-portal">
      {/* Mobile header */}
      <header className="md:hidden bg-slate-900 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <button onClick={() => setSidebarOpen(o => !o)} className="p-2 -ml-2"><Menu size={20} /></button>
        <div className="font-bold">EntoRid Admin</div>
        <button onClick={handleLogout} className="p-2 -mr-2"><LogOut size={18} /></button>
      </header>

      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'block' : 'hidden'} md:flex md:h-dvh md:w-60 md:flex-col ${isSuper ? 'bg-gradient-to-b from-purple-900 via-slate-900 to-slate-900' : 'bg-slate-900'} text-white flex-shrink-0`} data-testid="admin-main-sidebar">
        <div className="px-5 py-6 border-b border-white/10 hidden md:block">
          <div className="text-xl font-bold tracking-tight">EntoRid</div>
          <div className={`text-[11px] uppercase tracking-widest mt-0.5 ${isSuper ? 'text-purple-300' : 'text-emerald-300'}`}>
            {isSuper ? 'Super Admin Console' : 'Admin Portal'}
          </div>
          <div className="text-xs text-white/60 mt-3">
            <div className="font-semibold text-white flex items-center gap-1.5">
              {user?.name}
              {isSuper && <Shield size={11} className="text-purple-300" />}
            </div>
            <div className="truncate">{user?.email}</div>
          </div>
        </div>
        <nav className="p-3 space-y-1 md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain" data-testid="admin-main-menu-scroll">
          {NAV_GROUPS.map(([groupLabel, ids]) => {
            const items = ids.map((id) => TABS.find((item) => item.id === id)).filter(Boolean);
            if (!items.length) return null;
            return <section key={groupLabel} className="mb-5" data-testid={`admin-nav-group-${groupLabel.toLowerCase().replaceAll(' ', '-')}`}><p className="mx-2 mb-2 border-l-2 border-emerald-400 bg-white/10 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/90 shadow-sm">{groupLabel}</p>{items.map(({ id, label, Icon, group }) => <button
              key={id}
              onClick={() => { setTab(id); setSidebarOpen(false); }}
              data-testid={`admin-tab-${id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                tab === id
                  ? (group === 'platform' ? 'bg-purple-600 text-white font-semibold' : 'bg-emerald-600 text-white font-semibold')
                  : 'text-white/75 hover:bg-white/10'
              }`}
            >
              <Icon size={16} />
              {label}
              {group === 'platform' && tab !== id && <span className="ml-auto text-[9px] uppercase font-bold text-purple-300/70">SA</span>}
            </button>)}</section>;
          })}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-300 hover:bg-red-500/15 mt-6 md:flex hidden md:flex"
            data-testid="admin-logout"
          >
            <LogOut size={16} /> Logout
          </button>
        </nav>
      </aside>

      <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8 overflow-x-auto md:min-h-0 md:overflow-y-auto md:overscroll-contain" data-testid="admin-content-scroll">
        {tab === 'overview' && <OverviewTab api={api} onJump={setTab} />}
        {tab === 'analytics' && <AnalyticsDashboard api={api} />}
        {tab === 'pipeline' && <BookingPipelineTab api={api} />}
        {tab === 'lead-management' && <LeadManagementTab api={api} />}
        {tab === 'bookings' && <BookingsTab api={api} />}
        {tab === 'work-orders' && <WorkOrdersTab api={api} />}
        {tab === 'approvals' && <ApprovalsAdminTab api={api} onJump={setTab} />}
        {tab === 'warranties' && <WarrantyAdminTab api={api} isSuper={isSuper} />}
        {tab === 'amc' && <AMCTab api={api} />}
        {tab === 'customers' && <CustomersTab api={api} />}
        {tab === 'accounts' && <CustomerAccountsTab api={api} />}
        {/* Leads merged into Booking Pipeline */}
        {tab === 'tickets' && <TicketsAdminTab api={api} />}
        {tab === 'served-customers' && <ServedCustomersTab api={api} />}
        {tab === 'service-tickets' && <ServiceTicketsAdminTab api={api} isSuper={isSuper} />}
        {tab === 'quotations' && <QuotationsTab api={api} />}
        {tab === 'invoices' && <InvoicesAdminTab api={api} isSuper={isSuper} />}
        {tab === 'payments' && <PaymentsTab api={api} />}
        {tab === 'coupons' && <AdminCoupons token="entorid-admin-2026" />}
        {tab === 'pricing-calc' && <PricingCalculatorAdminTab api={api} />}
        {tab === 'staff-tools' && (isSuper ? <StaffToolsAdminTab api={api} /> : <div className="bg-white rounded-xl border border-slate-200 p-5"><h2 className="text-lg font-bold text-slate-900 mb-1">EntoRid Staff Tools</h2><p className="text-sm text-slate-500 mb-4">Your authorized tools — click to open.</p><StaffToolsLauncher api={api} /></div>)}
        {tab === 'special-offers' && <SpecialOffersAdminTab api={api} />}
        {tab === 'blog-news' && <BlogAdminTab api={api} />}
        {tab === 'video-testimonials' && <VideoTestimonialsAdminTab api={api} />}
        {tab === 'hero-videos' && <HeroVideoAdminTab api={api} />}
        {tab === 'seasonal-themes' && <ThemeCampaignAdminTab api={api} />}
        {tab === 'inventory' && <InventoryTab api={api} />}
        {tab === 'whatsapp' && <WhatsAppManagementTab api={api} isSuper={isSuper} />}
        {tab === 'recycle-bin' && <RecycleBinTab api={api} currentUser={user} />}
        {tab === 'incentives' && <IncentivesAdminTab />}
        {tab === 'reviews' && <ReviewsTab api={api} />}
        {tab === 'homepage-testimonials' && <HomepageTestimonialsAdminTab api={api} />}
        {tab === 'employees' && <EmployeesTab api={api} />}
        {tab === 'audit' && <AuditTab api={api} />}
        {tab === 'permissions' && isSuper && <PermissionsMatrixTab api={api} />}
        {tab === 'customer-login-management' && isSuper && <CustomerLoginManagementTab api={api} />}
        {tab === 'promo-banner' && isSuper && <PromoBannerTab api={api} />}
        {tab === 'payment-panel' && isSuper && <PaymentPanelTab api={api} />}
        {tab === 'system-health' && isSuper && <SystemHealthTab api={api} />}
        {tab === 'branding' && isSuper && <BrandingTab api={api} />}
        {tab === 'backups' && isSuper && <BackupsTab api={api} />}
        {tab === 'full-audit' && isSuper && <FullAuditTab api={api} />}
        {tab === 'email-settings' && isSuper && <EmailSettingsTab api={api} />}
        {tab === 'settings' && isSuper && <SettingsTab api={api} />}
      </main>
    </div>
    </DeleteConfirmProvider>
  );
}

/* ---------------- OVERVIEW ---------------- */
function OverviewTab({ api, onJump }) {
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('30d');  // today | 7d | 30d | 90d | all | custom
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const RANGE_OPTIONS = [
    { id: 'today', label: 'Today' },
    { id: '7d', label: 'Last 7d' },
    { id: '30d', label: 'Last 30d' },
    { id: '90d', label: 'Last 90d' },
    { id: 'all', label: 'All time' },
    { id: 'custom', label: 'Custom' },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ range });
      if (range === 'custom') {
        if (customFrom) qs.set('date_from', customFrom);
        if (customTo) qs.set('date_to', customTo);
      }
      // Independent fetches so one failing endpoint does NOT blank the other.
      const [a, b] = await Promise.allSettled([
        api('GET', `/admin/overview?${qs.toString()}`),
        api('GET', `/admin/dashboard/today?${qs.toString()}`),
      ]);
      setData(a.status === 'fulfilled' ? a.value.data : null);
      setToday(b.status === 'fulfilled' ? b.value.data : null);
    } finally { setLoading(false); }
  }, [api, range, customFrom, customTo]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-center py-20 text-slate-500">Loading overview…</div>;

  // CLICKABLE big stats — each jumps to its detail tab
  const stats = [
    { label: 'Total Leads', value: data?.leads_total || 0, sub: `${data?.leads_new || 0} new`, Icon: Users, color: 'from-blue-500 to-blue-700', tab: 'inbox' },
    { label: 'Total Bookings', value: data?.bookings_total || 0, sub: 'Paid customers', Icon: BookCheck, color: 'from-emerald-500 to-emerald-700', tab: 'bookings' },
    { label: 'Total Revenue', value: fmtMoney(data?.revenue), sub: `${data?.pending_payments || 0} pending`, Icon: IndianRupee, color: 'from-amber-500 to-amber-700', tab: 'invoices' },
    { label: 'Employees', value: data?.employees_total || 0, sub: `${data?.employees_active || 0} active`, Icon: UserCog, color: 'from-purple-500 to-purple-700', tab: 'employees' },
  ];

  const v = data?.visitors || {};

  const todayCards = [
    { label: "Today's Bookings", value: today?.todays_bookings || 0, sub: `${today?.todays_completed || 0} completed`, color: 'bg-blue-50 text-blue-700', tab: 'bookings' },
    { label: "Today's Revenue", value: fmtMoney(today?.todays_revenue), sub: 'Paid today', color: 'bg-emerald-50 text-emerald-700', tab: 'invoices' },
    { label: 'Pending Leads', value: today?.pending_leads || 0, sub: 'Awaiting follow-up', color: 'bg-amber-50 text-amber-700', tab: 'inbox' },
    { label: '🔥 Hot Leads', value: today?.hot_leads || 0, sub: 'High intent — call now', color: 'bg-rose-50 text-rose-700', tab: 'inbox' },
    { label: 'Warranty Renewals', value: today?.warranty_renewals_due || 0, sub: 'Due in 30 days', color: 'bg-teal-50 text-teal-700', tab: 'warranties' },
    { label: 'Pending Payments', value: today?.pending_payments || 0, sub: 'Not yet paid', color: 'bg-slate-50 text-slate-700', tab: 'invoices' },
  ];

  const engagement = [
    { l: 'Page Views', v: v.pageviews || 0, tab: 'analytics' },
    { l: 'Call Clicks', v: v.call_clicks || 0, tab: 'analytics' },
    { l: 'WhatsApp Clicks', v: v.whatsapp_clicks || 0, tab: 'analytics' },
    { l: 'Form Submits', v: v.form_submits || 0, tab: 'inbox' },
  ];

  return (
    <div data-testid="overview-tab">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time business metrics · {RANGE_OPTIONS.find(o => o.id === range)?.label}</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      </div>

      {/* Time range filter */}
      <div className="bg-white border border-slate-200 rounded-xl p-2 mb-5 inline-flex flex-wrap items-center gap-1" data-testid="overview-range">
        {RANGE_OPTIONS.map(opt => (
          <button
            key={opt.id}
            onClick={() => setRange(opt.id)}
            data-testid={`range-${opt.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              range === opt.id ? 'bg-emerald-700 text-white shadow' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
        {range === 'custom' && (
          <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-slate-200">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 text-xs border border-slate-200 rounded px-2" data-testid="range-from" />
            <span className="text-slate-400 text-xs">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 text-xs border border-slate-200 rounded px-2" data-testid="range-to" />
            <Button size="sm" onClick={load} className="h-8 px-3 bg-emerald-700 hover:bg-emerald-800">Apply</Button>
          </div>
        )}
      </div>

      {/* Lifetime stats — now CLICKABLE */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map(s => (
          <button
            key={s.label}
            onClick={() => onJump?.(s.tab)}
            data-testid={`stat-card-${s.label.toLowerCase().replace(/\s/g, '-')}`}
            className={`text-left rounded-2xl p-5 text-white bg-gradient-to-br ${s.color} shadow hover:shadow-lg hover:scale-[1.02] active:scale-95 transition cursor-pointer group`}
            title={`Open ${s.label} →`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-wider font-semibold opacity-90">{s.label}</p>
              <s.Icon size={20} className="opacity-80 group-hover:scale-110 transition" />
            </div>
            <p className="text-3xl font-extrabold mt-3" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>{s.value}</p>
            <p className="text-xs opacity-80 mt-1 flex items-center gap-1">{s.sub} <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition" /></p>
          </button>
        ))}
      </div>

      {/* Today's actionable widgets */}
      <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">📌 Today &amp; Action Items</h3>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {todayCards.map(c => (
          <button
            key={c.label}
            onClick={() => onJump?.(c.tab)}
            data-testid={`today-${c.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            className="text-left rounded-2xl p-4 border border-slate-200 hover:border-emerald-300 hover:shadow-md transition bg-white group cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{c.label}</p>
              <ArrowRight size={14} className="text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
            </div>
            <p className={`text-2xl sm:text-3xl font-extrabold mt-2 ${c.color} inline-block px-2 py-0.5 rounded-md`}>{c.value}</p>
            <p className="text-xs text-slate-500 mt-1">{c.sub}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900">On-site Engagement</h3>
          <button onClick={() => onJump?.('analytics')} className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="engagement-more">View analytics <ArrowRight size={11} /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {engagement.map(s => (
            <button
              key={s.l}
              onClick={() => onJump?.(s.tab)}
              className="bg-slate-50 hover:bg-emerald-50 rounded-xl p-4 text-left transition cursor-pointer group"
              data-testid={`engage-${s.l.toLowerCase().replace(/\s/g, '-')}`}
            >
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold flex items-center justify-between">{s.l}<ArrowRight size={11} className="opacity-0 group-hover:opacity-100" /></p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{(s.v || 0).toLocaleString()}</p>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={() => onJump?.('coupons')}
        className="w-full text-left bg-white rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-md p-5 transition cursor-pointer group"
        data-testid="coupons-card"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Coupons</h3>
          <ArrowRight size={14} className="text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
        </div>
        <p className="text-sm text-slate-600 mt-1">Total redemptions: <span className="font-bold">{data?.coupon_redemptions || 0}</span></p>
      </button>
    </div>
  );
}

/* ---------------- LEADS (legacy iframe shim) ---------------- */
function LeadsTab() {
  return (
    <iframe
      title="Leads"
      src="/admin"
      className="w-full min-h-[80vh] rounded-2xl border border-slate-200 bg-white"
      data-testid="leads-iframe"
    />
  );
}
function PaymentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState({ scope: 'all', from: null, to: null, slotKey: 'any', search: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (t, m) => { setToast({ t, m }); setTimeout(() => setToast(null), 3000); };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments`, {
        headers: { 'X-Admin-Token': 'entorid-admin-2026' },
      });
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  let filtered = rows;
  if (statusFilter !== 'all') filtered = filtered.filter((r) => (r.status || '') === statusFilter);
  filtered = applyTimeFilter(filtered, timeFilter, {
    dateField: 'created_at',
    fallbackDateField: 'created_at',
    searchFields: ['name', 'phone', 'order_id', 'payment_id', 'email'],
    ignoreSlot: true,
  });

  const totalRevenue = filtered.filter(r => r.status === 'paid').reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const paidCount = filtered.filter(r => r.status === 'paid').length;

  const exportCsv = () => {
    if (!filtered.length) return;
    const head = 'When,Customer,Phone,Order ID,Original,Discount,Paid,Status\n';
    const body = filtered.map((p) => [
      p.created_at ? new Date(p.created_at).toLocaleString('en-IN') : '',
      `"${(p.name || '').replace(/"/g, '""')}"`,
      p.phone || '',
      p.order_id || '',
      p.original_amount || p.amount || 0,
      p.discount_amount || 0,
      p.amount || 0,
      p.status || '',
    ].join(',')).join('\n');
    const blob = new Blob([head + body], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid="payments-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Payments</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} of {rows.length} order{rows.length === 1 ? '' : 's'} · ₹{totalRevenue.toLocaleString('en-IN')} revenue · {paidCount} paid</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <BookingsTimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            hideSlot
            label="Date"
            searchPlaceholder="Name, phone, order_id, payment_id…"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 border border-slate-200 rounded-lg text-sm"
            data-testid="payments-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="created">Created</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
          <Button variant="outline" onClick={exportCsv} data-testid="payments-export-csv" disabled={!filtered.length}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>
      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
        filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="payments-empty">
            <CreditCard size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No payments {rows.length ? 'match the current filter' : 'yet'}.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="payments-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">When</th>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Order ID</th>
                  <th className="px-4 py-3 text-right">Original</th>
                  <th className="px-4 py-3 text-right">Discount</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Mode</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <div>{p.created_at ? new Date(p.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div>
                      {p.paid_at && (
                        <div className="text-[10px] text-emerald-700 font-semibold mt-0.5" title="Payment received at">
                          ✓ Paid: {new Date(p.paid_at).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><div className="font-semibold">{p.name || '—'}</div><div className="text-xs text-slate-500">{p.phone || '—'}</div></td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{p.order_id}</td>
                    <td className="px-4 py-3 text-right">₹{(p.original_amount || p.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right text-emerald-700">{p.discount_amount ? `−₹${p.discount_amount.toLocaleString('en-IN')}` : '—'}</td>
                    <td className="px-4 py-3 text-right font-bold">₹{(p.amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : p.status === 'created' ? 'bg-amber-100 text-amber-700' : p.status === 'refunded' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{p.status}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-700 capitalize">{p.payment_mode || (p.status === 'paid' ? 'razorpay' : '—')}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setEditTarget(p)} data-testid={`payment-edit-${p.id}`}>
                        <Edit3 size={12} className="mr-1" /> Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <PaymentStatusModal
        open={!!editTarget}
        payment={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { showToast('success', 'Payment status updated'); setEditTarget(null); load(); }}
      />

      {toast && (
        <div className={`fixed bottom-6 right-6 z-[100] rounded-lg p-3 text-sm flex items-center gap-2 shadow-lg ${toast.t === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {toast.t === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {toast.m}
        </div>
      )}
    </div>
  );
}

function PaymentStatusModal({ open, payment, onClose, onSaved }) {
  const [form, setForm] = useState({ status: 'paid', payment_mode: 'cash', payment_id: '', amount: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !payment) return;
    setForm({
      status: payment.status || 'paid',
      payment_mode: payment.payment_mode || (payment.status === 'paid' ? 'razorpay' : 'cash'),
      payment_id: payment.payment_id || '',
      amount: payment.amount || '',
      notes: payment.admin_notes || '',
    });
    setErr('');
  }, [open, payment]);

  if (!open || !payment) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setErr(''); setBusy(true);
    try {
      const payload = {
        status: form.status,
        payment_mode: form.payment_mode,
        payment_id: form.payment_id || null,
        notes: form.notes || null,
      };
      if (form.amount !== '' && form.amount !== null) payload.amount = Number(form.amount);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${payment.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Token': 'entorid-admin-2026' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Update failed (${res.status})`); }
      onSaved?.();
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-edit-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900">Update Payment Status</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Order: <span className="font-mono">{payment.order_id}</span> · {payment.name || 'Customer'}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><XCircle size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs">Status</Label>
            <select value={form.status} onChange={update('status')} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="payment-edit-status">
              <option value="created">Created (awaiting payment)</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Payment Mode</Label>
            <select value={form.payment_mode} onChange={update('payment_mode')} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="payment-edit-mode">
              <option value="cash">Cash</option>
              <option value="upi">UPI (manual)</option>
              <option value="bank_transfer">Bank Transfer / NEFT / IMPS</option>
              <option value="cheque">Cheque</option>
              <option value="razorpay">Razorpay (online)</option>
              <option value="card">Card / POS</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input type="number" value={form.amount} onChange={update('amount')} data-testid="payment-edit-amount" />
            </div>
            <div>
              <Label className="text-xs">Reference / Txn ID</Label>
              <Input value={form.payment_id} onChange={update('payment_id')} placeholder="UPI ref / cheque no…" data-testid="payment-edit-ref" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Admin Notes</Label>
            <textarea value={form.notes} onChange={update('notes')} rows={2} className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="e.g. Received cash on-site by Rakesh on 22-Jun" data-testid="payment-edit-notes" />
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-2" data-testid="payment-edit-error"><AlertCircle size={14} /> {err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="payment-edit-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Saving…</> : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- BOOKINGS (Phase 2) ---------------- */
const BOOKING_STATUS_META = {
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700' },
  on_the_way: { label: 'On the Way', cls: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'In Progress', cls: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700' },
  warranty_active: { label: 'Warranty Active', cls: 'bg-teal-100 text-teal-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700' },
};

function BookingsTab({ api }) {
  const { user } = usePanelAuth();
  const isSuper = user?.role === 'super_admin';
  const [rows, setRows] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState({ scope: 'all', from: null, to: null, slotKey: 'any', search: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (t, m) => { setToast({ t, m }); setTimeout(() => setToast(null), 3000); };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, e] = await Promise.all([
        api('GET', '/admin/bookings'),
        api('GET', '/admin/employees-mini'),
      ]);
      setRows(b.data || []); setTechs(e.data || []);
    } catch (err) { showToast('error', formatApiError(err)); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  let filtered = filter === 'all' ? rows : rows.filter((r) => r.status === filter);
  if (sourceFilter !== 'all') filtered = filtered.filter((r) => (r.lead_source || 'website') === sourceFilter);
  filtered = applyTimeFilter(filtered, timeFilter);

  const handleUpdate = async (booking, patch) => {
    try {
      await api('PATCH', `/admin/bookings/${booking.id}`, patch);
      showToast('success', 'Booking updated');
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };

  return (
    <div data-testid="bookings-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Bookings</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} of {rows.length} booking{rows.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <BookingsTimeFilter value={timeFilter} onChange={setTimeFilter} />
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="bookings-source-filter">
            <option value="all">All sources</option>
            <option value="website">Website</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="phone_call">Phone Call</option>
            <option value="manual">Manual</option>
            <option value="walk_in">Walk-in</option>
            <option value="referral">Referral</option>
            <option value="social_media">Social Media</option>
            <option value="chatbot">Chatbot</option>
            <option value="other">Other</option>
          </select>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="bookings-filter">
            <option value="all">All statuses</option>
            {Object.entries(BOOKING_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bookings-create-btn">
            <UserPlus size={14} className="mr-1.5" /> New Booking
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>

      {toast && (
        <div className={`mb-4 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.t === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {toast.t === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {toast.m}
        </div>
      )}

      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
        filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="bookings-empty">
            <BookCheck size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No bookings {filter !== 'all' ? `with status "${BOOKING_STATUS_META[filter]?.label}"` : 'yet'}.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {filtered.map((b) => <BookingCard key={b.id} booking={b} techs={techs} onUpdate={handleUpdate} onDeleted={load} api={api} isSuper={isSuper} onCodeChanged={load} />)}
          </div>
        )
      }

      <CreateBookingModal
        open={showCreate}
        api={api}
        onClose={() => setShowCreate(false)}
        onCreated={() => { showToast('success', 'Booking created'); load(); }}
      />
    </div>
  );
}

function BookingDeleteButton({ booking, onDeleted }) {
  const { confirm } = useDeleteConfirm();
  return (
    <button
      onClick={async () => {
        const ok = await confirm({ module: 'bookings', id: booking.id, name: `${booking.booking_code} · ${booking.customer_name}` });
        if (ok && onDeleted) onDeleted();
      }}
      className="w-9 h-9 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 inline-flex items-center justify-center"
      data-testid={`booking-delete-${booking.booking_code}`}
      title="Move to Recycle Bin"
    >
      <Trash2 size={15} />
    </button>
  );
}

function BookingCard({ booking, techs, onUpdate, onDeleted, api, isSuper, onCodeChanged }) {
  const meta = BOOKING_STATUS_META[booking.status] || { label: booking.status, cls: 'bg-slate-100 text-slate-700' };
  const slotLabel = booking.time_slot_start && booking.time_slot_end
    ? `${booking.time_slot_start} – ${booking.time_slot_end}`
    : (booking.scheduled_time_slot || '—');
  const [showEditCode, setShowEditCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(booking.booking_code || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* ignore */ }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm" data-testid={`booking-${booking.booking_code}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-bold text-slate-900" data-testid={`booking-code-${booking.booking_code}`}>{booking.booking_code}</span>
            <button
              type="button"
              onClick={doCopy}
              title={copied ? 'Copied!' : 'Copy Booking No.'}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 transition"
              data-testid={`booking-code-copy-${booking.booking_code}`}
            >
              {copied ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Copy size={12} />}
            </button>
            {isSuper && (
              <button
                type="button"
                onClick={() => setShowEditCode(true)}
                title="Super Admin: Edit Booking No."
                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-slate-400 hover:text-purple-700 hover:bg-purple-50 transition"
                data-testid={`booking-code-edit-${booking.booking_code}`}
              >
                <Pencil size={12} />
              </button>
            )}
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
            <LeadSourceBadge source={booking.lead_source} />
            {booking.coupon_code && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{booking.coupon_code}</span>}
          </div>
          {booking.booking_code_previous && (
            <div className="mt-1 text-[10px] text-slate-500" data-testid={`booking-code-prev-${booking.booking_code}`}>
              Previously known as <span className="font-mono line-through">{booking.booking_code_previous}</span>
              {booking.booking_code_changed_at && (
                <> · changed {new Date(booking.booking_code_changed_at).toLocaleDateString('en-IN')}</>
              )}
            </div>
          )}
          <div className="mt-1 font-semibold text-slate-900">{booking.customer_name} · {booking.service_label}</div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
            <span className="flex items-center gap-1"><Phone size={12} /> {booking.customer_phone}</span>
            <span className="flex items-center gap-1"><MapPin size={12} /> {booking.customer_city || '—'}</span>
            <span className="flex items-center gap-1"><Calendar size={12} /> {booking.scheduled_date}</span>
            <span className="flex items-center gap-1 font-semibold text-emerald-700"><Clock size={12} /> {slotLabel}</span>
          </div>
        </div>
        <div className="text-right flex items-start gap-2">
          <div>
            <div className="text-xs text-slate-500">Final</div>
            <div className="text-xl font-extrabold text-emerald-700">₹{(booking.final_amount || 0).toLocaleString('en-IN')}</div>
          </div>
          <BookingDeleteButton booking={booking} onDeleted={onDeleted} />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Assign Technician</Label>
          <select
            value={booking.technician_id || ''}
            onChange={(e) => onUpdate(booking, { technician_id: e.target.value || null })}
            className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
            data-testid={`booking-tech-${booking.booking_code}`}
          >
            <option value="">— Unassigned —</option>
            {techs.map(t => <option key={t.id} value={t.id}>{t.name} ({t.employee_code})</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Scheduled Date</Label>
          <Input type="date" value={booking.scheduled_date || ''} onChange={(e) => onUpdate(booking, { scheduled_date: e.target.value })} className="mt-1 h-9 text-sm" data-testid={`booking-date-${booking.booking_code}`} />
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Time Slot</Label>
          <select
            value={booking.time_slot_start && booking.time_slot_end ? `${booking.time_slot_start}-${booking.time_slot_end}` : 'custom'}
            onChange={(e) => {
              if (e.target.value === 'custom') return;
              const [s, en] = e.target.value.split('-');
              onUpdate(booking, { time_slot_start: s, time_slot_end: en });
            }}
            className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
            data-testid={`booking-slot-${booking.booking_code}`}
          >
            <option value="08:00-10:00">8 – 10 AM</option>
            <option value="10:00-12:00">10 AM – 12 PM</option>
            <option value="12:00-14:00">12 – 2 PM</option>
            <option value="14:00-16:00">2 – 4 PM</option>
            <option value="16:00-18:00">4 – 6 PM</option>
            <option value="18:00-20:00">6 – 8 PM</option>
            <option value="custom">Custom…</option>
          </select>
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</Label>
          <select
            value={booking.status}
            onChange={(e) => onUpdate(booking, { status: e.target.value })}
            className="mt-1 w-full h-9 border border-slate-200 rounded-md px-2 text-sm"
            data-testid={`booking-status-${booking.booking_code}`}
          >
            {Object.entries(BOOKING_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>
      {booking.technician_name && (
        <div className="mt-3 text-xs text-slate-600 flex items-center gap-1.5">
          <Wrench size={13} /> Assigned to <span className="font-semibold">{booking.technician_name}</span> ({booking.technician_phone})
        </div>
      )}
      {showEditCode && isSuper && (
        <EditBookingCodeDialog
          booking={booking}
          api={api}
          onClose={() => setShowEditCode(false)}
          onSaved={() => { setShowEditCode(false); onCodeChanged?.(); }}
        />
      )}
    </div>
  );
}

/* ---------------- EDIT BOOKING NUMBER DIALOG (Super Admin only) ---------------- */
function EditBookingCodeDialog({ booking, api, onClose, onSaved }) {
  const [newCode, setNewCode] = useState(booking.booking_code || '');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = async () => {
    try {
      const r = await api('GET', `/admin/bookings/${booking.id}/booking-code/history`);
      setHistory(r.data || []);
      setShowHistory(true);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const submit = async () => {
    setErr('');
    if (!confirming) { setConfirming(true); return; }
    setSaving(true);
    try {
      await api('PATCH', `/admin/bookings/${booking.id}/booking-code`, {
        new_code: newCode.trim().toUpperCase(),
        reason: reason.trim() || null,
      });
      onSaved?.();
    } catch (e) {
      setErr(formatApiError(e));
      setConfirming(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/60 flex items-center justify-center p-4" data-testid="edit-booking-code-dialog">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-5 py-4 flex items-center gap-2">
          <Crown size={18} />
          <div>
            <h3 className="font-bold text-lg leading-tight">Edit Booking No.</h3>
            <p className="text-xs text-purple-100">Super Admin only — this action is audited.</p>
          </div>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-white/10" data-testid="edit-booking-code-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-500">Current Booking No.</div>
          <div className="font-mono text-sm font-bold text-slate-900">{booking.booking_code}</div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">New Booking No.</Label>
            <Input
              value={newCode}
              onChange={(e) => { setNewCode(e.target.value.toUpperCase()); setConfirming(false); }}
              className="mt-1 font-mono uppercase h-11"
              placeholder="BK260825100"
              maxLength={20}
              data-testid="edit-booking-code-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">Format: <code>BKYYMMDDXXX</code> (e.g. BK260825100)</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 h-11"
              placeholder="e.g. Duplicate reconciliation"
              maxLength={500}
              data-testid="edit-booking-code-reason"
            />
          </div>
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-start gap-1.5" data-testid="edit-booking-code-error">
              <AlertCircle size={13} className="mt-0.5 shrink-0" /> {err}
            </div>
          )}
          {confirming && !err && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="edit-booking-code-confirm">
              You are changing a permanent Booking Number. This action will be recorded in the audit history. The old code <code className="font-mono">{booking.booking_code}</code> will be retired and can never be reused. Continue?
            </div>
          )}
          <button
            type="button"
            onClick={loadHistory}
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="edit-booking-code-history-toggle"
          >
            <History size={12} /> {showHistory ? 'Hide' : 'Show'} audit history
          </button>
          {showHistory && (
            <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2" data-testid="edit-booking-code-history">
              {history.length === 0 && <div className="text-slate-500 italic">No previous changes.</div>}
              {history.map((h, i) => (
                <div key={i} className="border-b last:border-b-0 pb-1">
                  <div className="font-mono"><span className="line-through text-slate-500">{h.previous_code || '—'}</span> → <b>{h.new_code}</b></div>
                  <div className="text-slate-500">
                    {new Date(h.at).toLocaleString('en-IN')} · by {h.changed_by_name}
                    {h.reason && <> · <em>{h.reason}</em></>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1" data-testid="edit-booking-code-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !newCode.trim() || newCode.trim().toUpperCase() === booking.booking_code}
              className={`flex-1 font-bold ${confirming ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}`}
              data-testid="edit-booking-code-submit"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : (confirming ? 'Confirm Change' : 'Save…')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- WARRANTIES (Phase 3) ---------------- */
function WarrantiesTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alerts'); // alerts | active | expired | all
  const [toast, setToast] = useState(null);
  const showToast = (t, m) => { setToast({ t, m }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = filter === 'alerts' ? '/admin/warranties/alerts' : '/admin/warranties';
      const res = await api('GET', path);
      let data = res.data || [];
      if (filter === 'active') data = data.filter(r => r.status === 'active');
      else if (filter === 'expired') data = data.filter(r => r.status === 'expired');
      setRows(data);
    } catch (e) { showToast('error', formatApiError(e)); setRows([]); }
    finally { setLoading(false); }
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  const markReminded = async (w) => {
    try {
      await api('POST', `/admin/warranties/${w.id}/mark-reminded`);
      showToast('success', 'Marked as reminded');
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };

  return (
    <div data-testid="warranties-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Warranty Management</h1>
          <p className="text-sm text-slate-500 mt-1">Auto-created when a booking completes. Renew before expiry to retain customers.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="warranty-filter">
            <option value="alerts">⏰ Expiring Soon (30d)</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="all">All warranties</option>
          </select>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>

      {toast && (
        <div className={`mb-4 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.t === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {toast.t === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {toast.m}
        </div>
      )}

      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
        rows.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="warranties-empty">
            <ShieldCheck size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{filter === 'alerts' ? 'No warranties expiring in the next 30 days. 🎉' : 'No warranties yet. They auto-create when bookings complete.'}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="warranties-list">
            {rows.map((w) => {
              const days = w.days_remaining;
              const expired = w.status === 'expired';
              const urgent = !expired && days !== undefined && days <= 7;
              return (
                <div key={w.id} className={`bg-white rounded-2xl p-5 border-2 ${expired ? 'border-red-200 bg-red-50/30' : urgent ? 'border-amber-300' : 'border-slate-200'}`} data-testid={`warranty-${w.booking_code}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold">{w.booking_code}</span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${expired ? 'bg-red-100 text-red-700' : urgent ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {expired ? 'Expired' : urgent ? `${days}d left` : (w.status || '').toUpperCase()}
                        </span>
                        {w.reminder_sent_at && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Reminded</span>}
                      </div>
                      <div className="mt-1 font-semibold text-slate-900">{w.customer_name} · {w.service_label}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                        <span>📱 {w.customer_phone}</span>
                        <span>🛡 {w.days} days</span>
                        <span>📅 Started {(w.started_at || '').slice(0, 10)}</span>
                        <span>⏰ Ends {(w.ends_at || '').slice(0, 10)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={`tel:${w.customer_phone}`} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`warranty-call-${w.booking_code}`}>
                        <Phone size={12} /> Call to Renew
                      </a>
                      <a href={`https://api.whatsapp.com/send?phone=91${w.customer_phone}&text=${encodeURIComponent(`Hi ${w.customer_name}! Your ${w.service_label} warranty from EntoRid is expiring on ${(w.ends_at || '').slice(0,10)}. Would you like to schedule a renewal service?`)}`} target="_blank" rel="noreferrer" className="px-3 py-2 bg-[#25D366] text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5">
                        WhatsApp
                      </a>
                      {!w.reminder_sent_at && (
                        <button onClick={() => markReminded(w)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`warranty-remind-${w.booking_code}`}>
                          <Bell size={12} /> Mark reminded
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

/* ---------------- CUSTOMERS (Phase 2) ---------------- */
function CustomersTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState({ scope: 'all', from: null, to: null, slotKey: 'any', search: '' });
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api('GET', '/admin/customers'); setRows(r.data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const open = async (cust) => {
    setActive(cust);
    setDetail(null);
    try {
      const r = await api('GET', `/admin/customers/${cust.phone}`);
      setDetail(r.data);
    } catch (e) { setDetail({ error: formatApiError(e) }); }
  };

  const filtered = applyTimeFilter(rows, timeFilter, {
    dateField: 'last_booked_at',
    fallbackDateField: 'created_at',
    searchFields: ['name', 'phone', 'email', 'city'],
    ignoreSlot: true,
  });

  return (
    <div data-testid="customers-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} of {rows.length} unique customer{rows.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <BookingsTimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            hideSlot
            label="Date"
            searchPlaceholder="Name, phone, email, city…"
          />
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>

      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
        filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{rows.length === 0 ? 'No customers yet. They appear after the first successful payment.' : 'No matches.'}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Phone</th><th className="px-4 py-3 text-left">City</th><th className="px-4 py-3 text-right">Bookings</th><th className="px-4 py-3 text-right">Spent</th><th className="px-4 py-3 text-left">Last Booked</th></tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => open(c)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`customer-row-${c.phone}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{c.name}</td>
                    <td className="px-4 py-3">{c.phone}</td>
                    <td className="px-4 py-3">{c.city}</td>
                    <td className="px-4 py-3 text-right">{c.total_bookings}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-semibold">₹{(c.total_spent || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{c.last_booked_at ? new Date(c.last_booked_at).toLocaleDateString('en-IN') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {active && (
        <CustomerDrawer customer={active} detail={detail} api={api} onClose={() => { setActive(null); setDetail(null); }} onUpdated={(c) => setActive(c)} />
      )}
    </div>
  );
}

function CustomerDrawer({ customer, detail, api, onClose, onUpdated }) {
  const [notes, setNotes] = useState(detail?.customer?.notes || customer.notes || '');
  const [followUp, setFollowUp] = useState((detail?.customer?.follow_up_at || customer.follow_up_at || '').slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [tWindow, setTWindow] = useState('30d');

  useEffect(() => {
    setNotes(detail?.customer?.notes || customer.notes || '');
    setFollowUp((detail?.customer?.follow_up_at || customer.follow_up_at || '').slice(0, 10));
  }, [detail, customer]);

  // Load activity timeline whenever window changes
  useEffect(() => {
    let cancel = false;
    (async () => {
      setTimeline(null);
      try {
        const r = await api('GET', `/admin/customers/${customer.phone}/timeline?window=${tWindow}`);
        if (!cancel) setTimeline(r.data);
      } catch (e) { if (!cancel) setTimeline({ events: [], error: formatApiError(e) }); }
    })();
    return () => { cancel = true; };
  }, [customer.phone, tWindow, api]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api('PATCH', `/admin/customers/${customer.phone}`, { notes, follow_up_at: followUp || null });
      onUpdated(r.data);
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex justify-end" onClick={onClose} data-testid="customer-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg h-full overflow-y-auto">
        <div className="p-5 border-b sticky top-0 bg-white z-10 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold">{customer.name}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{customer.phone} · {customer.city}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-slate-50 rounded-lg p-3"><div className="text-2xl font-bold">{customer.total_bookings || 0}</div><div className="text-[10px] uppercase text-slate-500 font-semibold">Bookings</div></div>
            <div className="bg-emerald-50 rounded-lg p-3"><div className="text-2xl font-bold text-emerald-700">₹{(customer.total_spent || 0).toLocaleString('en-IN')}</div><div className="text-[10px] uppercase text-slate-500 font-semibold">Spent</div></div>
            <div className="bg-slate-50 rounded-lg p-3"><div className="text-sm font-bold">{customer.first_booked_at ? new Date(customer.first_booked_at).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : '—'}</div><div className="text-[10px] uppercase text-slate-500 font-semibold">Since</div></div>
          </div>

          {/* Activity Timeline (Phase 3) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-sm flex items-center gap-1.5"><Activity size={14} /> Activity Timeline</h4>
              <select value={tWindow} onChange={(e) => setTWindow(e.target.value)} className="h-8 px-2 border border-slate-200 rounded text-xs" data-testid="timeline-window">
                <option value="today">Today</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="1y">Last 1 year</option>
                <option value="all">All time</option>
              </select>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="timeline-list">
              {timeline === null && <p className="text-xs text-slate-400">Loading…</p>}
              {timeline && timeline.events?.length === 0 && <p className="text-xs text-slate-400">No activity in this window.</p>}
              {timeline?.events?.map((e, i) => (
                <div key={i} className="flex gap-2 text-xs">
                  <span className="text-base flex-shrink-0">{e.icon}</span>
                  <div className="flex-1 bg-slate-50 rounded-lg p-2">
                    <div className="font-semibold text-slate-800">{e.title}</div>
                    {e.detail && <div className="text-slate-500 mt-0.5">{e.detail}</div>}
                    <div className="text-[10px] text-slate-400 mt-0.5">{e.at ? new Date(e.at).toLocaleString('en-IN') : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase">Internal Notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1 w-full border border-slate-200 rounded-lg p-2 text-sm" placeholder="Customer preferences, special instructions…" data-testid="cust-notes" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase">Follow-up Date</Label>
            <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="mt-1" data-testid="cust-followup" />
          </div>
          <Button disabled={saving} onClick={save} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="cust-save">{saving ? 'Saving…' : 'Save Customer Info'}</Button>

          <div>
            <h4 className="font-bold text-sm mb-2">Booking History</h4>
            <div className="space-y-2">
              {(detail?.bookings || []).map(b => (
                <div key={b.id} className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between"><span className="font-mono font-bold text-xs">{b.booking_code}</span><span className="text-xs">{new Date(b.created_at).toLocaleDateString('en-IN')}</span></div>
                  <div className="mt-0.5">{b.service_label} — <span className="text-emerald-700 font-semibold">₹{(b.final_amount || 0).toLocaleString('en-IN')}</span></div>
                  <div className="text-xs text-slate-500">{BOOKING_STATUS_META[b.status]?.label || b.status} · {b.scheduled_date}</div>
                </div>
              ))}
              {!detail?.bookings?.length && <p className="text-xs text-slate-400">No bookings.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- LEAD INBOX (Phase 2) ---------------- */
const LEAD_STATUS_META = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contacted', cls: 'bg-amber-100 text-amber-700' },
  quoted: { label: 'Quoted', cls: 'bg-purple-100 text-purple-700' },
  won: { label: 'Won', cls: 'bg-emerald-100 text-emerald-700' },
  lost: { label: 'Lost', cls: 'bg-red-100 text-red-700' },
  paid: { label: 'Paid', cls: 'bg-emerald-200 text-emerald-800' },
};

function LeadInboxTab({ api }) {
  const [rows, setRows] = useState([]);
  const [hotIds, setHotIds] = useState(new Set());
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState({ scope: 'all', from: null, to: null, slotKey: 'any', search: '' });
  const [hotOnly, setHotOnly] = useState(false);
  const [active, setActive] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (t, m) => { setToast({ t, m }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, hotRes, empsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, { headers: { 'X-Admin-Token': 'entorid-admin-2026' } }).then(r => r.json()),
        api('GET', '/admin/leads/hot').then(r => r.data).catch(() => []),
        api('GET', '/admin/employees-mini').then(r => r.data).catch(() => []),
      ]);
      setRows(Array.isArray(leadsRes) ? leadsRes : []);
      setHotIds(new Set((hotRes || []).map(h => h.id)));
      setEmployees(empsRes || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  let filtered = filter === 'all' ? rows : rows.filter(r => (r.status || 'new') === filter);
  if (sourceFilter !== 'all') filtered = filtered.filter(r => (r.lead_source || r.source || 'website') === sourceFilter);
  if (hotOnly) filtered = filtered.filter(r => hotIds.has(r.id));
  filtered = applyTimeFilter(filtered, timeFilter, {
    dateField: 'created_at',
    fallbackDateField: 'created_at',
    searchFields: ['name', 'phone', 'email', 'city', 'pest_label', 'pest_type', 'additional_details', 'segment', 'industry_field'],
    ignoreSlot: true,
  });

  const updateStatus = async (lead, status, note) => {
    try {
      await api('PATCH', `/admin/leads/${lead.id}/status`, { status, note });
      showToast('success', `Lead marked ${LEAD_STATUS_META[status]?.label || status}`);
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };
  const addNote = async (lead, note) => {
    try {
      await api('POST', `/admin/leads/${lead.id}/notes`, { note });
      showToast('success', 'Note added');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, { headers: { 'X-Admin-Token': 'entorid-admin-2026' } }).then(x => x.json());
      const fresh = r.find(l => l.id === lead.id);
      if (fresh) setActive(fresh);
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };
  const assignLead = async (lead, employeeId, reason) => {
    try {
      const res = await api('POST', `/admin/leads/${lead.id}/assign`, { assigned_to: employeeId, reason });
      showToast('success', `Assigned to ${res.data?.assignment?.assigned_to_name || 'employee'}`);
      // Refresh the active drawer
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, { headers: { 'X-Admin-Token': 'entorid-admin-2026' } }).then(x => x.json());
      const fresh = r.find(l => l.id === lead.id);
      if (fresh) setActive(fresh);
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };

  return (
    <div data-testid="inbox-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Lead Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">{filtered.length} of {rows.length} leads · <span className="text-rose-600 font-semibold">🔥 {hotIds.size} hot</span></p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <BookingsTimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            hideSlot
            label="Date"
            searchPlaceholder="Name, phone, email, city, pest…"
          />
          <button onClick={() => setHotOnly(h => !h)} className={`h-10 px-3 rounded-lg text-sm font-semibold border ${hotOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-slate-700 border-slate-200'}`} data-testid="inbox-hot-filter">
            🔥 Hot only
          </button>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="inbox-source-filter">
            <option value="all">All sources</option>
            <option value="website">Website</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="phone_call">Phone Call</option>
            <option value="manual">Manual</option>
            <option value="walk_in">Walk-in</option>
            <option value="referral">Referral</option>
            <option value="social_media">Social Media</option>
            <option value="chatbot">Chatbot</option>
            <option value="other">Other</option>
          </select>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 px-3 border border-slate-200 rounded-lg text-sm" data-testid="inbox-filter">
            <option value="all">All statuses</option>
            {Object.entries(LEAD_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="inbox-create-btn">
            <UserPlus size={14} className="mr-1.5" /> New Lead
          </Button>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
        </div>
      </div>

      {toast && (
        <div className={`mb-4 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.t === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {toast.t === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {toast.m}
        </div>
      )}

      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
        filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <Inbox size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No leads.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="inbox-list">
            {filtered.map(l => {
              const meta = LEAD_STATUS_META[l.status || 'new'] || LEAD_STATUS_META.new;
              const isHot = hotIds.has(l.id);
              return (
                <div key={l.id} onClick={() => setActive(l)} className={`bg-white rounded-xl border-2 p-4 hover:border-emerald-300 cursor-pointer ${isHot ? 'border-rose-300 bg-rose-50/30' : 'border-slate-200'}`} data-testid={`inbox-item-${l.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isHot && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-sm" data-testid={`hot-badge-${l.id}`}>
                            <Flame size={10} /> Hot
                          </span>
                        )}
                        <span className="font-semibold text-slate-900">{l.name}</span>
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                        {l.segment && l.segment !== 'residential' && (
                          <span
                            data-testid={`segment-badge-${l.id}`}
                            className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                              l.segment === 'commercial'
                                ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                                : 'bg-purple-50 text-purple-700 ring-1 ring-purple-200'
                            }`}
                          >
                            {l.segment === 'commercial' ? '🏢' : '🏭'} {l.segment}
                          </span>
                        )}
                        {l.segment === 'residential' && (
                          <span data-testid={`segment-badge-${l.id}`} className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 ring-1 ring-sky-200">🏠 residential</span>
                        )}
                        <LeadSourceBadge source={l.lead_source || l.source} />
                        {l.assignment?.assigned_to_name && (
                          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">👤 {l.assignment.assigned_to_name}</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                        <span>📱 {l.phone}</span>
                        <span>🏙 {l.city}</span>
                        <span>🐛 {l.pest_label || l.pest_type}</span>
                        {l.industry_field && <span>🏷 {l.industry_field}</span>}
                        {l.address?.pincode && <span data-testid={`lead-pin-${l.id}`}>📍 {l.address.pincode}{l.address.district ? ` · ${l.address.district}` : ''}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN') : ''}</div>
                      {l.estimated_price && <div className="text-sm font-bold text-emerald-700 mt-0.5">₹{Number(l.estimated_price).toLocaleString('en-IN')}</div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {active && <LeadDrawer
        lead={active}
        employees={employees}
        isHot={hotIds.has(active.id)}
        onClose={() => setActive(null)}
        onStatus={updateStatus}
        onNote={addNote}
        onAssign={assignLead}
        onConvert={() => setConvertTarget(active)}
        onDeleted={() => { setActive(null); load(); }}
      />}

      <CreateLeadModal
        open={showCreate}
        api={api}
        onClose={() => setShowCreate(false)}
        onCreated={() => { showToast('success', 'Lead created'); load(); }}
      />
      <ConvertLeadModal
        open={!!convertTarget}
        api={api}
        lead={convertTarget}
        onClose={() => setConvertTarget(null)}
        onConverted={(bk) => { showToast('success', `Booking ${bk?.booking_code} created`); setActive(null); load(); }}
      />
    </div>
  );
}

function LeadDrawer({ lead, employees, isHot, onClose, onStatus, onNote, onAssign, onConvert, onDeleted }) {
  const [note, setNote] = useState('');
  const [statusVal, setStatusVal] = useState(lead.status || 'new');
  const [assignee, setAssignee] = useState(lead.assignment?.assigned_to || '');
  const [reason, setReason] = useState('');
  const { confirm: confirmDelete } = useDeleteConfirm();
  useEffect(() => setStatusVal(lead.status || 'new'), [lead.status]);
  useEffect(() => setAssignee(lead.assignment?.assigned_to || ''), [lead.assignment]);
  const meta = LEAD_STATUS_META[statusVal] || LEAD_STATUS_META.new;
  const alreadyConverted = lead.status === 'converted' || !!lead.booking_id;

  const handleDelete = async () => {
    const ok = await confirmDelete({ module: 'leads', id: lead.id, name: lead.name });
    if (ok && onDeleted) onDeleted();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex justify-end" onClick={onClose} data-testid="lead-drawer">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg h-full overflow-y-auto">
        <div className="p-5 border-b sticky top-0 bg-white z-10 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold">{lead.name}</h3>
              {isHot && <span className="inline-flex items-center gap-0.5 text-[10px] uppercase font-extrabold px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white"><Flame size={10} /> Hot</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{lead.phone} · {lead.city}</div>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${meta.cls} mt-2 inline-block`}>{meta.label}</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="text-sm space-y-1">
            <div><span className="text-slate-500">Service:</span> {lead.pest_label || lead.pest_type}</div>
            {lead.property_size && <div><span className="text-slate-500">Property:</span> {lead.property_size}</div>}
            {lead.estimated_price && <div><span className="text-slate-500">Quoted:</span> ₹{Number(lead.estimated_price).toLocaleString('en-IN')}</div>}
            {lead.additional_details && <div><span className="text-slate-500">Notes:</span> {lead.additional_details}</div>}
            <div><span className="text-slate-500">Source:</span> {lead.source || 'website'}</div>
            <div><span className="text-slate-500">When:</span> {lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN') : '—'}</div>
          </div>

          {/* Structured address block — surfaced from calculator submission */}
          {lead.address && typeof lead.address === 'object' && (lead.address.state || lead.address.pincode || lead.address.line1) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="lead-address-block">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase text-emerald-900 mb-1.5">
                <MapPin size={12} /> Service Address
              </div>
              <div className="text-sm space-y-0.5 text-slate-700">
                {lead.address.line1 && <div>{lead.address.line1}</div>}
                {lead.address.landmark && <div className="text-xs text-slate-500">Near {lead.address.landmark}</div>}
                <div>
                  {[lead.address.city, lead.address.district].filter(Boolean).join(', ')}
                  {lead.address.state && <span> · {lead.address.state}</span>}
                  {lead.address.pincode && <span className="font-semibold"> — {lead.address.pincode}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const parts = [lead.address.line1, lead.address.landmark, lead.address.city, lead.address.district, lead.address.state, lead.address.pincode].filter(Boolean);
                  const q = encodeURIComponent(parts.join(', '));
                  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
                }}
                className="mt-2 text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                data-testid="lead-address-map-btn"
              >
                Open in Google Maps →
              </button>
            </div>
          )}

          {/* Assign to telecaller / sales executive */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3">
            <Label className="text-xs font-semibold uppercase text-indigo-900 flex items-center gap-1.5"><UserPlus size={12} /> Assign to Telecaller / Sales Exec</Label>
            {lead.assignment?.assigned_to_name ? (
              <div className="mt-2 text-sm">
                <span className="font-semibold">Currently with:</span> {lead.assignment.assigned_to_name} · {lead.assignment.assigned_to_phone}
                <div className="text-[11px] text-slate-500 mt-0.5">Assigned by {lead.assignment.assigned_by} on {new Date(lead.assignment.assigned_at).toLocaleString('en-IN')}</div>
              </div>
            ) : <div className="text-xs text-slate-500 mt-1">Not assigned yet.</div>}
            <div className="mt-2 flex gap-2">
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="flex-1 h-9 px-2 border border-indigo-200 rounded text-sm bg-white" data-testid="lead-assign-select">
                <option value="">— Select employee —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
              </select>
              <Button disabled={!assignee} onClick={() => onAssign(lead, assignee, reason)} className="bg-indigo-600 hover:bg-indigo-700 h-9 px-3 text-sm" data-testid="lead-assign-btn">Assign</Button>
            </div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason (e.g. Hindi speaker)" className="mt-2 h-8 text-xs" />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase">Status</Label>
            <select value={statusVal} onChange={(e) => { setStatusVal(e.target.value); onStatus(lead, e.target.value); }} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm" data-testid="lead-status-select">
              {Object.entries(LEAD_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>

          <div>
            <h4 className="font-bold text-sm mb-2 flex items-center gap-1.5"><StickyNote size={14} /> Notes</h4>
            <div className="space-y-2 mb-3">
              {(lead.notes || []).slice().reverse().map((n, i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="text-xs text-slate-500">{n.by} · {n.at ? new Date(n.at).toLocaleString('en-IN') : ''}</div>
                  <div className="mt-0.5">{n.note}</div>
                </div>
              ))}
              {!lead.notes?.length && <p className="text-xs text-slate-400">No notes yet.</p>}
            </div>
            <div className="flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="flex-1" data-testid="lead-note-input" />
              <Button disabled={!note.trim()} onClick={() => { onNote(lead, note); setNote(''); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="lead-note-add">Add</Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <a href={`tel:${lead.phone}`} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"><Phone size={14} /> Call</a>
            <a href={`https://api.whatsapp.com/send?phone=91${lead.phone}`} target="_blank" rel="noreferrer" className="px-3 py-2 bg-[#25D366] text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5">WhatsApp</a>
            {alreadyConverted ? (
              <span className="px-3 py-2 bg-emerald-100 text-emerald-800 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5" data-testid="lead-converted-badge">
                <CheckCircle2 size={14} /> Converted{lead.booking_code ? ` · ${lead.booking_code}` : ''}
              </span>
            ) : (
              <button onClick={() => onConvert?.(lead)} className="px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold inline-flex items-center gap-1.5" data-testid="lead-convert-btn">
                <ArrowRight size={14} /> Convert to Booking
              </button>
            )}
            <button onClick={handleDelete} className="px-3 py-2 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5" data-testid="lead-delete-btn" title="Move lead to Recycle Bin">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- EMPLOYEES (full CRUD) ---------------- */
function EmployeesTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [pwReset, setPwReset] = useState(null);

  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api('GET', '/admin/employees'); setRows(res.data || []); }
    catch (e) { showToast('error', formatApiError(e)); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    try {
      if (editing) await api('PATCH', `/admin/employees/${editing.id}`, payload);
      else await api('POST', '/admin/employees', payload);
      showToast('success', editing ? 'Employee updated' : 'Employee created');
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { showToast('error', formatApiError(e)); }
  };
  const handleDelete = async (emp) => {
    if (!window.confirm(`Delete ${emp.name}?`)) return;
    try { await api('DELETE', `/admin/employees/${emp.id}`); showToast('success', 'Deleted'); load(); }
    catch (e) { showToast('error', formatApiError(e)); }
  };
  const handleSuspend = async (emp) => {
    try { await api('PATCH', `/admin/employees/${emp.id}`, { status: emp.status === 'active' ? 'suspended' : 'active' }); load(); }
    catch (e) { showToast('error', formatApiError(e)); }
  };

  return (
    <div data-testid="employees-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Employee Management</h1>
          <p className="text-sm text-slate-500 mt-1">{rows.length} employee{rows.length === 1 ? '' : 's'}</p>
        </div>
        {!showForm && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-employee-btn">
            <UserPlus size={16} className="mr-1.5" /> New Employee
          </Button>
        )}
      </div>

      {toast && (
        <div className={`mb-4 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="employee-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {showForm && (
        <EmployeeForm
          initial={editing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}

      {loading && rows.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Loading…</div>
      ) : rows.length === 0 && !showForm ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="employees-empty">
          <UserCog size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 mb-3">No employees yet.</p>
          <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700"><UserPlus size={16} className="mr-1.5" /> Create your first employee</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((e) => (
            <EmployeeCard
              key={e.id}
              emp={e}
              onEdit={() => { setEditing(e); setShowForm(true); }}
              onDelete={() => handleDelete(e)}
              onSuspend={() => handleSuspend(e)}
              onResetPw={() => setPwReset(e)}
              onTogglePrivacy={async () => {
                try {
                  const next = !e.permissions?.show_full_customer_details;
                  await api('PATCH', `/admin/employees/${e.id}`, { show_full_customer_details: next });
                  showToast('success', next ? `Full customer details ENABLED for ${e.name}` : `Privacy mode ENABLED for ${e.name}`);
                  load();
                } catch (err) { showToast('error', formatApiError(err)); }
              }}
            />
          ))}
        </div>
      )}

      {pwReset && (
        <ResetPasswordModal
          emp={pwReset}
          onClose={() => setPwReset(null)}
          onSubmit={async (newPw) => {
            try { await api('POST', `/admin/employees/${pwReset.id}/reset-password`, { new_password: newPw }); showToast('success', `Password reset for ${pwReset.name}`); setPwReset(null); }
            catch (e) { showToast('error', formatApiError(e)); }
          }}
        />
      )}
    </div>
  );
}

const PERM_LABELS = {
  can_login: 'Can Login',
  can_access_dashboard: 'Dashboard Access',
  can_view_customers: 'View Customers',
  can_view_payments: 'View Payments',
  can_edit_bookings: 'Edit Bookings',
  can_create_reports: 'Create Reports',
  show_full_customer_details: 'Show Full Customer Details',
};

function EmployeeForm({ initial, onCancel, onSave }) {
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [mobile, setMobile] = useState(initial?.mobile || '');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [department, setDepartment] = useState(initial?.department || 'Operations');
  const [designation, setDesignation] = useState(initial?.designation || 'Technician');
  const [joiningDate, setJoiningDate] = useState(initial?.joining_date || new Date().toISOString().slice(0, 10));
  const [perms, setPerms] = useState(initial?.permissions || {
    can_login: true, can_access_dashboard: true, can_view_customers: false,
    can_view_payments: false, can_edit_bookings: true, can_create_reports: true,
    show_full_customer_details: false,
  });
  const [saving, setSaving] = useState(false);

  const togglePerm = (k) => setPerms(p => ({ ...p, [k]: !p[k] }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: name.trim(), email: email.trim().toLowerCase(), mobile: mobile.replace(/\D/g, '').slice(-10),
      department, designation, joining_date: joiningDate, permissions: perms,
    };
    if (!initial) payload.password = password;
    await onSave(payload);
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-6 border border-slate-200 mb-5" data-testid="employee-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">{initial ? `Edit Employee — ${initial.name}` : 'New Employee'}</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Full Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="emp-name" /></div>
        <div><Label>Email *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="emp-email" /></div>
        <div><Label>Mobile (10 digits) *</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} required data-testid="emp-mobile" /></div>
        {!initial && (
          <div>
            <Label>Initial Password (min 8) *</Label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="emp-password" />
              <button type="button" onClick={() => setShowPw(s => !s)} className="absolute inset-y-0 right-0 px-3 text-slate-500">{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
          </div>
        )}
        <div><Label>Department</Label><Input value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="emp-dept" /></div>
        <div><Label>Designation</Label><Input value={designation} onChange={(e) => setDesignation(e.target.value)} data-testid="emp-desg" /></div>
        <div><Label>Joining Date</Label><Input type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} data-testid="emp-joining" /></div>
      </div>
      <div className="mt-5">
        <Label>Permissions</Label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.keys(PERM_LABELS).filter(k => k !== 'show_full_customer_details').map(k => (
            <label key={k} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${perms[k] ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
              <input type="checkbox" checked={!!perms[k]} onChange={() => togglePerm(k)} className="w-4 h-4" data-testid={`perm-${k}`} />
              <span className="text-xs font-medium">{PERM_LABELS[k]}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Customer Privacy — dedicated bold control (governs what the technician sees on the Employee Portal) */}
      <div className="mt-5 p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200">
        <div className="flex items-start gap-3">
          <input
            id="show-full-cust"
            type="checkbox"
            checked={!!perms.show_full_customer_details}
            onChange={() => togglePerm('show_full_customer_details')}
            className="w-4 h-4 mt-1"
            data-testid="perm-show_full_customer_details"
          />
          <label htmlFor="show-full-cust" className="cursor-pointer">
            <div className="text-sm font-bold text-indigo-900">🔓 Show Full Customer Details to this Employee</div>
            <div className="text-xs text-indigo-700 mt-0.5">
              When <b>OFF</b> (recommended), this technician sees only Customer Name, Booking ID, Address and the &quot;Work Done&quot; status on their portal — phone and email stay hidden for privacy.
              When <b>ON</b>, the employee can see phone, email and call the customer directly.
            </div>
          </label>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="emp-save">
          {saving ? 'Saving…' : initial ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

function EmployeeCard({ emp, onEdit, onDelete, onSuspend, onResetPw, onTogglePrivacy }) {
  const suspended = emp.status === 'suspended';
  const fullDetails = !!emp.permissions?.show_full_customer_details;
  return (
    <div className={`bg-white rounded-xl border-2 p-5 ${suspended ? 'border-slate-200 opacity-75' : 'border-emerald-200'}`} data-testid={`emp-card-${emp.employee_code}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-slate-900">{emp.name}</span>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${suspended ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{emp.status}</span>
            <span
              className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${fullDetails ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-700'}`}
              title={fullDetails ? 'This employee sees phone, email and all booking details.' : 'This employee sees only Customer Name, Booking ID and Address (privacy mode).'}
              data-testid={`emp-privacy-badge-${emp.employee_code}`}
            >
              {fullDetails ? '🔓 Full Details' : '🔒 Privacy Mode'}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{emp.employee_code} · {emp.designation} · {emp.department}</div>
          <div className="text-xs text-slate-600 mt-1">{emp.email} · {emp.mobile}</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onTogglePrivacy}
            title={fullDetails ? 'Switch to Privacy Mode (hide phone & email from this employee)' : 'Allow this employee to see full customer details'}
            className={`p-2 rounded-md ${fullDetails ? 'text-indigo-700 hover:bg-indigo-50' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid={`emp-privacy-toggle-${emp.employee_code}`}
          >
            {fullDetails ? <Unlock size={15} /> : <Lock size={15} />}
          </button>
          <button onClick={onSuspend} title={suspended ? 'Activate' : 'Suspend'} className="p-2 rounded-md hover:bg-slate-100 text-slate-600" data-testid={`emp-toggle-${emp.employee_code}`}><Power size={15} /></button>
          <button onClick={onResetPw} title="Reset password" className="p-2 rounded-md hover:bg-blue-50 text-blue-600" data-testid={`emp-resetpw-${emp.employee_code}`}><KeyRound size={15} /></button>
          <button onClick={onEdit} title="Edit" className="p-2 rounded-md hover:bg-amber-50 text-amber-600"><Edit2 size={15} /></button>
          <button onClick={onDelete} title="Delete" className="p-2 rounded-md hover:bg-red-50 text-red-600"><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-[11px]">
        {Object.keys(PERM_LABELS).map(k => (
          <div key={k} className={`px-2 py-1 rounded ${emp.permissions?.[k] ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'}`}>
            {emp.permissions?.[k] ? '✓' : '·'} {PERM_LABELS[k]}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResetPasswordModal({ emp, onClose, onSubmit }) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center px-4" onClick={onClose} data-testid="reset-pw-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={async (e) => { e.preventDefault(); setBusy(true); await onSubmit(pw); setBusy(false); }} className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold mb-1">Reset Password</h3>
        <p className="text-sm text-slate-500 mb-4">for {emp.name} ({emp.employee_code})</p>
        <Label>New Password (min 8 chars)</Label>
        <div className="relative mt-1">
          <Input type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} minLength={8} required data-testid="new-pw" />
          <button type="button" onClick={() => setShow(s => !s)} className="absolute inset-y-0 right-0 px-3 text-slate-500">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || pw.length < 8} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reset-pw-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Reset Password
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ---------------- AUDIT LOG ---------------- */
function AuditTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api('GET', '/admin/audit-log'); setRows(res.data || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-tab">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Activity Log</h1>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      </div>
      {loading ? <div className="text-center py-10 text-slate-500">Loading…</div> :
        rows.length === 0 ? <div className="bg-white rounded-xl p-10 text-center text-slate-500 border border-dashed">No activity yet</div> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="px-4 py-3 text-left">When</th><th className="px-4 py-3 text-left">Action</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-left">IP</th><th className="px-4 py-3 text-left">User Agent</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 whitespace-nowrap text-slate-700">{new Date(r.created_at).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.action}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${r.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{r.success ? 'OK' : 'FAIL'}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-mono">{r.ip || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 max-w-[300px] truncate">{r.user_agent || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- SETTINGS (gateway + own password) ---------------- */
function SettingsTab({ api }) {
  const [toast, setToast] = useState(null);
  const showToast = (t, m) => { setToast({ t, m }); setTimeout(() => setToast(null), 3500); };

  const [gwCur, setGwCur] = useState('');
  const [gwNewEmail, setGwNewEmail] = useState('');
  const [gwNewPw, setGwNewPw] = useState('');
  const [gwSaving, setGwSaving] = useState(false);

  const [myCur, setMyCur] = useState('');
  const [myNew, setMyNew] = useState('');
  const [mySaving, setMySaving] = useState(false);

  const updateGw = async (e) => {
    e.preventDefault(); setGwSaving(true);
    try {
      await api('POST', '/admin/settings/gateway', { current_password: gwCur, new_email: gwNewEmail || undefined, new_password: gwNewPw || undefined });
      showToast('success', 'Gateway credentials updated. Use new credentials on next login.');
      setGwCur(''); setGwNewEmail(''); setGwNewPw('');
    } catch (e) { showToast('error', formatApiError(e)); } finally { setGwSaving(false); }
  };
  const updateMyPw = async (e) => {
    e.preventDefault(); setMySaving(true);
    try {
      await api('POST', '/change-password', { current_password: myCur, new_password: myNew });
      showToast('success', 'Your admin password was changed');
      setMyCur(''); setMyNew('');
    } catch (e) { showToast('error', formatApiError(e)); } finally { setMySaving(false); }
  };

  return (
    <div data-testid="settings-tab">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-5">Security Settings</h1>

      {toast && (
        <div className={`mb-4 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.t === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="settings-toast">
          {toast.t === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.m}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <form onSubmit={updateGw} className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="gw-settings-form">
          <h3 className="font-bold mb-3 text-slate-900">Official Gateway Credentials</h3>
          <p className="text-xs text-slate-500 mb-4">First-level entry to <code>/official</code>. Rotate periodically.</p>
          <div className="space-y-3">
            <div><Label>Current Gateway Password *</Label><Input type="password" value={gwCur} onChange={(e) => setGwCur(e.target.value)} required data-testid="gw-cur-pw" /></div>
            <div><Label>New Gateway Email</Label><Input type="email" value={gwNewEmail} onChange={(e) => setGwNewEmail(e.target.value)} placeholder="leave blank to keep current" data-testid="gw-new-email" /></div>
            <div><Label>New Gateway Password</Label><Input type="password" value={gwNewPw} onChange={(e) => setGwNewPw(e.target.value)} placeholder="leave blank to keep current" data-testid="gw-new-pw" /></div>
          </div>
          <Button type="submit" disabled={gwSaving || !gwCur || (!gwNewEmail && !gwNewPw)} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="gw-save">
            {gwSaving ? 'Saving…' : 'Update Gateway'}
          </Button>
        </form>

        <form onSubmit={updateMyPw} className="bg-white rounded-2xl p-5 border border-slate-200" data-testid="my-pw-form">
          <h3 className="font-bold mb-3 text-slate-900">My Admin Password</h3>
          <p className="text-xs text-slate-500 mb-4">Second-level credential for the Admin Portal.</p>
          <div className="space-y-3">
            <div><Label>Current Password *</Label><Input type="password" value={myCur} onChange={(e) => setMyCur(e.target.value)} required data-testid="my-cur-pw" /></div>
            <div><Label>New Password (min 8) *</Label><Input type="password" value={myNew} onChange={(e) => setMyNew(e.target.value)} required minLength={8} data-testid="my-new-pw" /></div>
          </div>
          <Button type="submit" disabled={mySaving || !myCur || myNew.length < 8} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="my-pw-save">
            {mySaving ? 'Saving…' : 'Change Password'}
          </Button>
        </form>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        Phase 3 will add: OTP-based reset, &quot;Logout all devices&quot;, IP/device tracking dashboard, force-reset all users.
      </p>
    </div>
  );
}
